import type { SpaceDerivationFrame, TransitionPhase } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toCameraViewId } from '@/workflow-runner/_pure/derivation/cameraGraph';
import type { Config } from '@/workflow-runner/_contract/config';

export function deriveRingTransferPhases(args: {
    frame: SpaceDerivationFrame;
    config: Config;
    stepKey: string;
    itemKey: `item:entity:${string}`;
    from: [number, number, number];
    to: [number, number, number];
    destinationType: string;
    color: string;
    transitionKey: string;
}): TransitionPhase[] {
    const fromAngle = Math.atan2(args.from[2], args.from[0]);
    const toAngle = Math.atan2(args.to[2], args.to[0]);
    if (Math.abs(Math.atan2(Math.sin(toAngle - fromAngle), Math.cos(toAngle - fromAngle))) < 1e-8) return [];
    const camera = args.config.adapterModeConfig.camera;
    const guided = camera?.navigationMode === 'guided';
    const duration = Math.max(args.config.adapterModeConfig.playback.resourceTypeRadialActivation.processingHoldMs,
        camera?.navigationDurationMs ?? 0);
    const view = toCameraViewId(args.stepKey, args.destinationType, 'folded');
    if (guided && !args.frame.spaceRenderOptions?.camera?.graph?.views[view]) {
        throw new Error(`Ring transfer cannot resolve destination camera view: ${view}`);
    }
    const pulse = args.config.adapterModeConfig.playback.resourceTypeRadialRipple.enabled;
    return [
        {
            transitionOptions: { transitionKind: 'instant', transitionKey: `${args.transitionKey}:ring-transfer:start` },
            patch: pulse ? { itemParametricState: { entityItemByKey: { [args.itemKey]: { ringPulse: {
                radius: Math.hypot(args.from[0], args.from[2]), height: args.from[1] + .1,
                fromAngle, toAngle, progress01: 0, color: args.color,
            } } } } } : {},
        },
        {
            transitionOptions: { transitionKind: 'interpolated', transitionKey: `${args.transitionKey}:resource-type-radial:processing-hold`,
                duration, easing: 'smoothstep' },
            ...(guided ? { cameraNavigation: { view, orbitCenter: [0, 0, 0] as [number, number, number] } } : {}),
            patch: pulse ? { itemParametricState: { entityItemByKey: { [args.itemKey]: { ringPulse: { progress01: 1 } } } } } : {},
        },
        {
            transitionOptions: { transitionKind: 'instant', transitionKey: `${args.transitionKey}:ring-transfer:clear` },
            patch: { itemParametricState: { entityItemByKey: { [args.itemKey]: { ringPulse: null } } } },
        },
    ];
}
